import { PropsWithChildren, useMemo, useState } from 'react'

import { Flex } from 'antd'

import { ContextMenu } from '@eastcoal/common/components/ContextMenu'
import { ContextMenuProps } from '@eastcoal/common/components/ContextMenu/interface'
import { getFirstScrolledParent } from '@eastcoal/common/utils/dom'
import { pick } from '@eastcoal/common/utils/object'

import { useEditor } from '../../hooks/useEditor'
import {
  $canMergeTableCells,
  $canUnmergeTableCells,
  $clearTableContentAtSelection,
  $deleteTableAtSelection,
  $deleteTableColsAtSelection,
  $deleteTableRowsAtSelection,
  $insertTableCol,
  $insertTableRow,
  $mergeTableCellsAtSelection,
  $unmergeTableCellsAtSelection,
  $updateTableColMarkedAtSelection,
  $updateTableMarkedAtSelection,
  $updateTableRowMarkedAtSelection
} from '../../utils/table'

import { TableContextMenuProps } from './interface'
import { RowCountInput } from './RowCountInput'
import { TableContextMenuProvider } from './Store'
import { useTableContextMenuStore } from './Store/hooks/useStore'

const INSERT_ROW_TOP = 'insert-row-top'
const INSERT_ROW_BOTTOM = 'insert-row-bottom'
const INSERT_COLUMN_LEFT = 'insert-column-left'
const INSERT_COLUMN_RIGHT = 'insert-column-right'
const DELETE_ROW = 'delete-row'
const DELETE_COLUMN = 'delete-column'
const DELETE_TABLE = 'delete-table'
const MERGE_CELLS = 'merge-cells'
const UNMERGE_CELLS = 'unmerge-cells'
const CLEAR_CONTENT = 'clear-content'

const TableContextMenuCore = ({
  onDeleteColumn,
  onDeleteRow,
  onInsertColumn,
  onInsertRow,
  children
}: PropsWithChildren<TableContextMenuProps>) => {
  const editor = useEditor()
  const {
    rowCountTop,
    rowCountBottom,
    columnCountLeft,
    columnCountRight,
    setRowColCount
  } = useTableContextMenuStore(state =>
    pick(state, [
      'rowCountTop',
      'rowCountBottom',
      'columnCountLeft',
      'columnCountRight',
      'setRowColCount'
    ])
  )

  const handleClick: ContextMenuProps['onClick'] = ({ key, domEvent }) => {
    editor.update(() => {
      switch (key) {
        case INSERT_ROW_TOP:
          $insertTableRow({
            shouldInsertAfter: false,
            rowCount: rowCountTop
          })
          setRowColCount({ rowCountTop: 1 })
          onInsertRow?.({ direction: 'top', count: rowCountTop })
          break
        case INSERT_ROW_BOTTOM:
          $insertTableRow({
            shouldInsertAfter: true,
            rowCount: rowCountBottom
          })
          setRowColCount({ rowCountBottom: 1 })
          onInsertRow?.({ direction: 'bottom', count: rowCountBottom })
          break
        case INSERT_COLUMN_LEFT:
          $insertTableCol({
            shouldInsertAfter: false,
            columnCount: columnCountLeft
          })
          setRowColCount({ columnCountLeft: 1 })
          onInsertColumn?.({ direction: 'left', count: columnCountLeft })
          break
        case INSERT_COLUMN_RIGHT:
          $insertTableCol({
            shouldInsertAfter: true,
            columnCount: columnCountRight
          })
          setRowColCount({ columnCountRight: 1 })
          onInsertColumn?.({ direction: 'right', count: columnCountRight })
          break
        case DELETE_ROW:
          $deleteTableRowsAtSelection()
          onDeleteRow?.()
          break
        case DELETE_COLUMN:
          $deleteTableColsAtSelection()
          onDeleteColumn?.()
          break
        case DELETE_TABLE:
          $deleteTableAtSelection(editor)
          break
        case MERGE_CELLS:
          $mergeTableCellsAtSelection()
          break
        case UNMERGE_CELLS:
          $unmergeTableCellsAtSelection()
          break
        case CLEAR_CONTENT:
          $clearTableContentAtSelection()
          break
      }
    })
  }

  const itemsGroup1 = useMemo(() => {
    const items: ContextMenuProps['items'] = [
      {
        key: INSERT_ROW_TOP,
        label: (
          <Flex align='center' gap={8}>
            <span>向上插入</span>
            <RowCountInput countKey='rowCountTop' />
            <span>行</span>
          </Flex>
        )
      },
      {
        key: INSERT_ROW_BOTTOM,
        label: (
          <Flex align='center' gap={8}>
            <span>向下插入</span>
            <RowCountInput countKey='rowCountBottom' />
            <span>行</span>
          </Flex>
        )
      },
      {
        key: INSERT_COLUMN_LEFT,
        label: (
          <Flex align='center' gap={8}>
            <span>向左插入</span>
            <RowCountInput countKey='columnCountLeft' />
            <span>列</span>
          </Flex>
        )
      },
      {
        key: INSERT_COLUMN_RIGHT,
        label: (
          <Flex gap={8}>
            <span>向右插入</span>
            <RowCountInput countKey='columnCountRight' />
            <span>列</span>
          </Flex>
        )
      },
      {
        key: 'divider1',
        type: 'divider'
      },
      {
        key: DELETE_ROW,
        label: '删除选中行',
        onMouseEnter() {
          editor.update(() => {
            $updateTableRowMarkedAtSelection(editor, true)
          })
        },
        onMouseLeave() {
          editor.update(() => {
            $updateTableRowMarkedAtSelection(editor, false)
          })
        }
      },
      {
        key: DELETE_COLUMN,
        label: '删除选中列',
        onMouseEnter() {
          editor.update(() => {
            $updateTableColMarkedAtSelection(editor, true)
          })
        },
        onMouseLeave() {
          editor.update(() => {
            $updateTableColMarkedAtSelection(editor, false)
          })
        }
      },
      {
        key: DELETE_TABLE,
        label: '删除表格',
        onMouseEnter() {
          editor.update(() => {
            $updateTableMarkedAtSelection(editor, true)
          })
        },
        onMouseLeave() {
          editor.update(() => {
            $updateTableMarkedAtSelection(editor, false)
          })
        }
      }
    ]
    return items
  }, [editor])

  const [itemsGroup2, setItemGroup2] = useState<ContextMenuProps['items']>([])

  const handleContextMenuClose = () => {
    setItemGroup2([])
  }

  const handleContextMenuOpen = () => {
    editor.getEditorState().read(() => {
      if ($canMergeTableCells()) {
        setItemGroup2([
          {
            key: 'divider2',
            type: 'divider'
          },
          {
            key: MERGE_CELLS,
            label: '合并单元格'
          }
        ])
      } else if ($canUnmergeTableCells()) {
        setItemGroup2([
          {
            key: 'divider2',
            type: 'divider'
          },
          {
            key: UNMERGE_CELLS,
            label: '拆分单元格'
          }
        ])
      }
    })
  }

  const itemsGroup3 = useMemo<ContextMenuProps['items']>(() => {
    return [
      {
        key: 'divider3',
        type: 'divider'
      },
      {
        key: CLEAR_CONTENT,
        label: '清除内容'
      }
    ]
  }, [])

  const mergedItems = useMemo(() => {
    return [...itemsGroup1, ...itemsGroup2, ...itemsGroup3]
  }, [itemsGroup1, itemsGroup2, itemsGroup3])

  return (
    <ContextMenu
      getPopupContainer={dom => getFirstScrolledParent(dom) || document.body}
      items={mergedItems}
      onClick={handleClick}
      onClose={handleContextMenuClose}
      onOpen={handleContextMenuOpen}
    >
      {children}
    </ContextMenu>
  )
}

export const TableContextMenu = ({
  children,
  ...restProps
}: PropsWithChildren<TableContextMenuProps>) => {
  return (
    <TableContextMenuProvider>
      <TableContextMenuCore {...restProps}>{children}</TableContextMenuCore>
    </TableContextMenuProvider>
  )
}
